/**
 * "กำลังจัดส่ง" in the customer tab: one answer per conversation from GET /pos/conversations/:id/deliveries.
 * While the card is on screen it refreshes every POLL_MS (the API caches 45 s and refreshes the POS behind a stale
 * answer, so a poll never waits on a slow POS), pauses while the browser tab is hidden and catches up when it comes
 * back. `pending` (the POS has not answered the first time yet) is asked again after PENDING_RETRY_MS.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getDeliveriesApi, type Deliveries } from '../api/deliveries'
import { useInbox } from './inbox'

export const POLL_MS = 60_000
export const PENDING_RETRY_MS = 5_000

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))

export const useDeliveries = defineStore('deliveries', () => {
  const data = ref<Record<string, Deliveries>>({})
  const loading = ref<Record<string, boolean>>({})
  const error = ref<Record<string, string | null>>({})
  const loadedAt = ref<Record<string, number>>({})
  /** Asked again while a load was running (e.g. the chat was just linked): run once more when it lands (fresh if
   *  any of those asks was "โหลดใหม่"). */
  const again = new Map<string, boolean>()
  let watching: string | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let onVisibility: (() => void) | null = null

  const hidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden'

  async function load(id: string, fresh = false): Promise<void> {
    if (loading.value[id]) {
      again.set(id, fresh || !!again.get(id)) // that answer may be for the customer set before the change
      return
    }
    loading.value[id] = true
    try {
      data.value[id] = await getDeliveriesApi().get(id, fresh)
      error.value[id] = null
    } catch (e) {
      error.value[id] = errText(e) || 'โหลดสถานะขนส่งไม่สำเร็จ'
    } finally {
      loading.value[id] = false
      loadedAt.value[id] = Date.now()
    }
    if (again.has(id)) {
      const next = fresh || !!again.get(id)
      again.delete(id)
      void load(id, next)
      return
    }
    if (watching === id) schedule(id)
  }

  function schedule(id: string): void {
    if (timer) clearTimeout(timer)
    const wait = data.value[id]?.state === 'pending' ? PENDING_RETRY_MS : POLL_MS
    timer = setTimeout(() => {
      timer = null
      if (watching !== id) return
      if (hidden()) return // resumes on visibilitychange
      void load(id)
    }, wait)
  }

  /** Chats whose orders changed while their card was not on screen (another chat, the order tab open, a resync): the
   *  next showing asks the POS fresh, not the API's cached answer. */
  const stale = new Set<string>()
  let rtOff: (() => void) | null = null
  /** A chat order of the shown chat changed (saved / held / paid / edited / linked — api/app/pos_api.py notify_order, on
   *  every screen): its "รอชำระ" rows and deliveries are asked from the POS at once, not at the next poll (owner 18 ต.ค. 69
   *  "กดยืนยันชำระเงินแล้วแต่หน้าลูกค้าไม่อัพเดทต้องรีเฟส"); any other chat is asked fresh when shown again. A resync
   *  (reconnect, the tab visible again — events may have been missed) does the same for every chat. */
  function listen(): void {
    if (rtOff) return
    rtOff = useInbox().onRealtime((e) => {
      if (!e) {
        for (const id of Object.keys(data.value)) if (id !== watching) stale.add(id)
        if (watching) void load(watching, true)
        return
      }
      if (e.type !== 'conversation.updated' || !e.pos_order) return
      const id = e.conversation.id
      if (id === watching) void load(id, true)
      else stale.add(id)
    })
  }

  /** Show this conversation's card (null = none): load now, then keep it fresh while visible. */
  function watch(id: string | null): void {
    stop()
    watching = id
    if (!id) return
    listen()
    if (typeof document !== 'undefined') {
      onVisibility = () => {
        if (watching !== id || hidden()) return
        const age = Date.now() - (loadedAt.value[id] ?? 0)
        if (age >= POLL_MS) void load(id)
        else schedule(id)
      }
      document.addEventListener('visibilitychange', onVisibility)
    }
    void load(id, stale.delete(id))
  }

  function stop(): void {
    watching = null
    if (timer) clearTimeout(timer)
    timer = null
    if (onVisibility && typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility)
    onVisibility = null
  }

  /** "โหลดใหม่": ask the POS now (the API allows it at most every 10 s per customer). */
  function refresh(id: string): void {
    void load(id, true)
  }

  return { data, loading, error, loadedAt, load, watch, stop, refresh }
})
